import { Outlet } from 'react-router-dom';
import {
  FileText,
  Settings,
  Palette,
  Briefcase,
  Inbox,
  Award,
  Receipt,
  Sparkles,
  BookOpen,
  MoreHorizontal,
} from 'lucide-react';
import { Suspense, useMemo, useState } from 'react';
import { NavigationDrawer, DesktopHeader, Spinner } from '@elka/ui';
import type { NavItemConfig } from '@elka/ui';
import { useAuth } from '../features/auth/hooks/useAuth';
import { useGmailEnabled } from '../hooks/useGmailEnabled';
import { useTheme } from '@elka/theme';
import { Footer } from './Footer';

// Three primary destinations + a utility overflow group. The pipeline/board view lives
// inside the "עסקאות" section (toggled via DealsViewToggle), and the quick-quote / quick-bill
// flows are in-section action buttons (DealsPage / QuotesPage / BillsPage) — neither needs a
// top-level nav entry. Keeps the daily surface to three tabs instead of nine.
const NAV_ITEMS: NavItemConfig[] = [
  { to: '/', label: 'עסקאות', icon: Briefcase },
  { to: '/quotes', label: 'הצעות מחיר', icon: FileText },
  { to: '/bills', label: 'חשבונות עסקה', icon: Receipt },
  {
    to: '#',
    label: 'עוד',
    icon: MoreHorizontal,
    subItems: [
      { to: '/suppliers/scorecards', label: 'ניקוד ספקים', icon: Award },
      { to: '/ai', label: 'תמחור AI', icon: Sparkles },
      { to: '/dekel', label: 'מחירון דקל', icon: BookOpen },
      { to: '/inbound-emails', label: 'אימיילים נכנסים', icon: Inbox },
      { to: '/settings', label: 'הגדרות', icon: Settings },
    ],
  },
];

export function Layout() {
  const { signOut, user } = useAuth();
  const { theme, colors, toggleTheme } = useTheme();
  const { enabled: gmailEnabled, isLoading: gmailLoading } = useGmailEnabled();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Drop the Gmail-only "inbound emails" entry for tenants without features.gmail.
  // Keep it while the flag is still resolving to avoid a show→hide flicker.
  const baseItems = useMemo(() => {
    const hideGmail = !gmailLoading && !gmailEnabled;
    if (!hideGmail) return NAV_ITEMS;
    return NAV_ITEMS.map((item) =>
      item.subItems
        ? { ...item, subItems: item.subItems.filter((s) => s.to !== '/inbound-emails') }
        : item
    );
  }, [gmailEnabled, gmailLoading]);

  const navItems = baseItems.map((item, i) => ({
    ...item,
    color: i % 2 === 0 ? colors.navProjects : colors.navClients,
    // Root tab matches "/" exactly, otherwise it stays highlighted on every nested route.
    end: item.to === '/',
  }));

  const displayName = user?.displayName?.split(' ')[0] || user?.email?.split('@')[0] || '';
  const userInitial = displayName.charAt(0).toUpperCase();

  return (
    <div className="app-bg flex flex-col" style={{ minHeight: '100vh' }}>
      <DesktopHeader
        title="הצעות מחיר אלקה"
        mobileTitle="הצעות מחיר"
        subtitle="ניהול הצעות מחיר"
        logoHref="/"
        navItems={navItems}
        user={user ? { displayName: user.displayName, photoURL: user.photoURL } : null}
        onLogout={signOut}
        onMobileMenuOpen={() => setIsMobileMenuOpen(true)}
      />

      <NavigationDrawer
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        items={baseItems}
        footerContent={
          <button
            onClick={toggleTheme}
            className={`relative flex w-full items-center justify-center gap-2 overflow-hidden rounded-xl px-4 py-3 font-semibold text-white shadow-md transition-transform hover:scale-[1.02] active:scale-[0.98] ${theme === 'elkaBlue' ? 'bg-gradient-to-r from-amber-500 via-yellow-500 to-orange-500' : 'bg-gradient-to-r from-[#0C1A55] via-[#3C5FA8] to-[#0C1A55]'}`}
          >
            <Palette size={18} />
            <span>{theme === 'elkaBlue' ? 'זהב' : 'כחול'}</span>
          </button>
        }
        user={{ name: displayName, initial: userInitial, photoURL: user?.photoURL }}
        onSignOut={signOut}
      />

      <main className="flex-1 p-3 md:p-5 lg:p-7">
        <div className="mx-auto max-w-7xl">
          <Suspense
            fallback={
              <div className="flex min-h-[40vh] items-center justify-center">
                <Spinner size="lg" />
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </div>
      </main>

      <Footer />
    </div>
  );
}
